/* =============================================================================
   Vantage — overlays: drawers, modals, disclosures, sticky header
   Copyright (c) 2026. All rights reserved.
   ========================================================================== */

(function (V) {
  'use strict';

  /* ---------------------------------------------------------------------
     <vt-drawer> — a panel that slides in from an edge.

     <vt-drawer id="cart" data-hydrate="idle">
       <button data-drawer-open>Open</button>
       <div class="scrim" data-drawer-close></div>
       <div class="drawer drawer--right" role="dialog" aria-modal="true">…</div>
     </vt-drawer>

     Any element anywhere on the page carrying
     `data-drawer-target="cart"` will also open it.
     --------------------------------------------------------------------- */

  var VtDrawer = class extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer');
      this.release = null;

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-open]')) return this.open();
        if (event.target.closest('[data-drawer-close]')) return this.close();
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });

      if (this.id) {
        this.remoteHandler = (event) => {
          var trigger = event.target.closest('[data-drawer-target="' + this.id + '"]');
          if (!trigger) return;
          event.preventDefault();
          this.open();
        };
        document.addEventListener('click', this.remoteHandler);
      }
    }

    disconnectedCallback() {
      if (this.remoteHandler) document.removeEventListener('click', this.remoteHandler);
      if (this.release) this.release();
      V.unlockScroll();
    }

    get isOpen() {
      return this.hasAttribute('data-open');
    }

    open() {
      if (this.isOpen) return;
      this.setAttribute('data-open', '');
      V.lockScroll();
      if (this.panel) this.release = V.trapFocus(this.panel);
      V.emit('drawer:open', { id: this.id, drawer: this });
    }

    close() {
      if (!this.isOpen) return;
      this.removeAttribute('data-open');
      V.unlockScroll();
      if (this.release) {
        this.release();
        this.release = null;
      }
      V.emit('drawer:close', { id: this.id, drawer: this });
    }

    toggle() {
      this.isOpen ? this.close() : this.open();
    }
  };

  V.define('vt-drawer', VtDrawer);

  /* ---------------------------------------------------------------------
     <vt-dropdown> — hover/click panel used by the desktop navigation.
     Opens on pointer for mice, on click for touch and keyboards.
     --------------------------------------------------------------------- */

  V.define(
    'vt-dropdown',
    class extends HTMLElement {
      connectedCallback() {
        this.trigger = this.querySelector('[aria-expanded]');
        this.panel = this.querySelector('.nav__panel');
        if (!this.trigger || !this.panel) return;

        this.closeTimer = null;
        this.hoverable = window.matchMedia('(hover: hover)').matches;

        this.trigger.addEventListener('click', (event) => {
          event.preventDefault();
          this.toggle();
        });

        if (this.hoverable) {
          this.addEventListener('pointerenter', () => this.show());
          this.addEventListener('pointerleave', () => this.scheduleHide());
        }

        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.hide();
        });

        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          this.hide();
          this.trigger.focus();
        });
      }

      show() {
        clearTimeout(this.closeTimer);
        document.querySelectorAll('vt-dropdown').forEach((other) => {
          if (other !== this && other.hide) other.hide();
        });
        this.panel.setAttribute('data-open', '');
        this.trigger.setAttribute('aria-expanded', 'true');
      }

      hide() {
        clearTimeout(this.closeTimer);
        this.panel.removeAttribute('data-open');
        this.trigger.setAttribute('aria-expanded', 'false');
      }

      scheduleHide() {
        this.closeTimer = setTimeout(() => this.hide(), 160);
      }

      toggle() {
        this.trigger.getAttribute('aria-expanded') === 'true' ? this.hide() : this.show();
      }
    }
  );

  /* ---------------------------------------------------------------------
     <vt-sticky-header> — hides on scroll down, reveals on scroll up.
     --------------------------------------------------------------------- */

  V.define(
    'vt-sticky-header',
    class extends HTMLElement {
      connectedCallback() {
        this.header = this.querySelector('.header') || this;
        this.lastY = window.scrollY;
        this.ticking = false;

        // Sticky positioning belongs on the section wrapper, which is the
        // only ancestor tall enough for the header to travel inside.
        if (this.header.classList.contains('header--sticky')) {
          var wrapper = this.closest('.shopify-section') || this.parentElement;
          if (wrapper) wrapper.classList.add('is-sticky');
        }

        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => this.update());
        };

        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
      }

      update() {
        this.ticking = false;
        if (!this.header.classList.contains('header--sticky')) return;
        var y = window.scrollY;
        var threshold = this.header.offsetHeight;

        this.header.classList.toggle('header--raised', y > 4);

        // Never hide while a dropdown or drawer is open.
        var blocked = this.querySelector('.nav__panel[data-open]') || document.querySelector('vt-drawer[data-open]');

        if (!blocked && y > threshold && y > this.lastY + 6) {
          this.header.classList.add('header--hidden');
        } else if (y < this.lastY - 6 || y <= threshold) {
          this.header.classList.remove('header--hidden');
        }

        this.lastY = y;
      }
    }
  );

  /* ---------------------------------------------------------------------
     <vt-accordion-group> — keeps only one <details> open at a time.
     --------------------------------------------------------------------- */

  V.define(
    'vt-accordion-group',
    class extends HTMLElement {
      connectedCallback() {
        this.addEventListener('toggle', (event) => {
          var opened = event.target;
          if (!opened.open) return;
          this.querySelectorAll('details[open]').forEach((other) => {
            if (other !== opened && other.parentElement === opened.parentElement) other.open = false;
          });
        }, true);
      }
    }
  );

  /* ---------------------------------------------------------------------
     <vt-slideshow> — crossfading slides with dots, arrows, keyboard and
     optional autoplay. Autoplay pauses on hover, on focus, when the tab is
     hidden, and whenever the visitor prefers reduced motion.
     --------------------------------------------------------------------- */

  V.define(
    'vt-slideshow',
    class extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('[data-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-slide-to]'));
        if (this.slides.length < 2) return;

        this.index = 0;
        this.timer = null;

        var prev = this.querySelector('[data-slide-prev]');
        var next = this.querySelector('[data-slide-next]');
        if (prev) prev.addEventListener('click', () => this.step(-1));
        if (next) next.addEventListener('click', () => this.step(1));

        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => this.go(Number(dot.dataset.slideTo)));
        });

        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') { event.preventDefault(); this.step(1); }
          if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-1); }
        });

        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.autoplay = this.dataset.autoplay === 'true' && !this.reduced;
        this.interval = Number(this.dataset.interval) || 6000;

        if (this.autoplay) {
          this.addEventListener('pointerenter', () => this.stop());
          this.addEventListener('pointerleave', () => this.start());
          this.addEventListener('focusin', () => this.stop());
          this.addEventListener('focusout', () => this.start());
          this.visibility = () => (document.hidden ? this.stop() : this.start());
          document.addEventListener('visibilitychange', this.visibility);
          this.start();
        }
      }

      disconnectedCallback() {
        this.stop();
        if (this.visibility) document.removeEventListener('visibilitychange', this.visibility);
      }

      go(index) {
        this.index = (index + this.slides.length) % this.slides.length;

        this.slides.forEach((slide, i) => {
          slide.toggleAttribute('data-active', i === this.index);
          // Off-screen slides must be unreachable by keyboard.
          slide.setAttribute('aria-hidden', String(i !== this.index));
        });

        this.dots.forEach((dot, i) => {
          dot.setAttribute('aria-selected', String(i === this.index));
        });
      }

      step(delta) {
        this.go(this.index + delta);
        if (this.autoplay) this.start();
      }

      start() {
        this.stop();
        if (!this.autoplay || document.hidden) return;
        this.timer = setInterval(() => this.go(this.index + 1), this.interval);
      }

      stop() {
        if (this.timer) clearInterval(this.timer);
        this.timer = null;
      }
    }
  );

  /* ---------------------------------------------------------------------
     <vt-live-search> — predictive results under the search input.
     --------------------------------------------------------------------- */

  V.define(
    'vt-live-search',
    class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="search"]');
        this.results = this.querySelector('[data-results]');
        if (!this.input || !this.results) return;

        this.input.addEventListener(
          'input',
          V.debounce(() => this.search(), 250)
        );

        this.input.addEventListener('blur', () => {
          setTimeout(() => this.results.classList.add('is-hidden'), 150);
        });
      }

      search() {
        var term = this.input.value.trim();

        if (term.length < 2) {
          this.results.innerHTML = '';
          this.results.classList.add('is-hidden');
          return;
        }

        var url =
          V.routes.predictiveSearch +
          '?q=' +
          encodeURIComponent(term) +
          '&resources[type]=product,collection,article&resources[limit]=6&section_id=predictive-search';

        fetch(url)
          .then((response) => response.text())
          .then((html) => {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var body = parsed.querySelector('[data-predictive-body]');
            this.results.innerHTML = body ? body.innerHTML : '';
            this.results.classList.remove('is-hidden');
          })
          .catch(() => {
            this.results.classList.add('is-hidden');
          });
      }
    }
  );
})(window.Vantage);
